import { createId } from '@paralleldrive/cuid2'
import { smallint, text, timestamp } from 'drizzle-orm/pg-core'

/**
 * Column builders every table repeats. Deliberately NOT re-exported from
 * `schema/index.ts`: that barrel is what `drizzle.config.ts` points at, and it
 * should describe tables, not helpers.
 *
 * Money is absent on purpose. Every amount is written out as
 * `bigint('…_net_minor', { mode: 'number' })` at its own call site so the unit
 * is visible in the schema and greppable in review — a `money()` helper would
 * hide exactly the thing that must never drift back to a formatted string.
 */

/**
 * Prefixed cuid2 primary key, e.g. `svc_x1y2…`. The prefix makes an id
 * self-describing in logs and URLs and makes a copy-pasted id from the wrong
 * table obvious. Generated in the app rather than by the database so an insert
 * knows the id before it round-trips.
 */
export function primaryId(prefix: string) {
  return text('id')
    .primaryKey()
    .$defaultFn(() => `${prefix}_${createId()}`)
}

export function createdAt() {
  return timestamp('created_at', { withTimezone: true }).notNull().defaultNow()
}

export function updatedAt() {
  return timestamp('updated_at', { withTimezone: true }).notNull().defaultNow()
}

/**
 * Polish VAT on creative services, in basis points (2300 = 23%). Stored per
 * row rather than read from a constant because a rate change must not silently
 * rewrite the tax on orders that were already placed.
 */
export const DEFAULT_TAX_RATE_BPS = 2300

export function taxRateBps() {
  return smallint('tax_rate_bps').notNull().default(DEFAULT_TAX_RATE_BPS)
}
